import { graphHooks } from '@tunasong/graph-lib/react'
import type { ChatGroup } from '@tunasong/plugin-chat/ui'
import type { Chat, Comment, Persisted } from '@tunasong/schemas'
import { useEffect, useState } from 'react'
import { CommentGroup } from './comment-group.js'
import { useOpenDocument } from './open-document-store.js'
import { commentGroupLimit, orderCommentGroups } from './order-comment-groups.js'

/** The batch endpoint takes the ids in its URL, which caps it at about 300 (reference: parentIds batch) */
const maxParentIds = 250

/**
 * The open song's anchored groups for the sidebar Chat (chat makeover M3, #2718). Two batch reads: the commented
 * blocks' chats, then those chats' comments (only `resolved` and `createdAt`, to order them). Each group then loads
 * its own thread. The reads are live queries, so a resolve or a new comment, which invalidate their tags, reorders.
 */
export const useCommentGroups = (entityId: string | undefined) => {
  const openDocument = useOpenDocument(entityId)
  const blocks = openDocument?.blocks ?? []
  /** Past the URL cap (a song with more than 250 commented blocks), the first ones in the song are left out */
  const blockIds = blocks.map(block => block.id).slice(-maxParentIds)
  const blockKey = blockIds.join(',')

  const [loadChats, chatsResult] = graphHooks.useLoadChildEntitiesBatch()
  const [loadComments, commentsResult] = graphHooks.useLoadChildEntitiesBatch()
  const [limit, setLimit] = useState(commentGroupLimit)

  useEffect(() => {
    if (blockKey) {
      void loadChats({ parentIds: blockKey.split(','), type: 'chat' }, true)
    }
  }, [blockKey, loadChats])

  const chats = (blockKey ? (chatsResult.data ?? []) : []) as Persisted<Chat>[]
  const chatKey = chats.map(chat => chat.id).join(',')

  useEffect(() => {
    if (chatKey) {
      void loadComments({ parentIds: chatKey.split(','), type: 'comment', fields: 'resolved,createdAt' }, true)
    }
  }, [chatKey, loadComments])

  const comments = (chatKey ? (commentsResult.data ?? []) : []) as Persisted<Comment>[]
  const { groups: ordered, older } = orderCommentGroups({ blocks, chats, comments, limit })
  const chatById = new Map(chats.map(chat => [chat.id, chat]))

  const groups: ChatGroup[] = ordered.flatMap(({ block, chatId, latestActivity }) => {
    const chat = chatById.get(chatId)
    return chat && openDocument
      ? [
          {
            id: chatId,
            latestActivity,
            content: <CommentGroup block={block} chat={chat} onShowInSong={() => openDocument.showInSong(block.id)} />,
          },
        ]
      : []
  })

  return { groups, older, loadOlder: () => setLimit(current => current + commentGroupLimit) }
}
